import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import { Fade } from 'react-reveal'

import LoadingBase from '../../loading/LoadingBase/LoadingBase'

import truncateString from '../../../services/data/truncateString'
import media from '../../../services/generateMediaQueries'

import ZingIcon from '../../../assets/img/zing-icon.svg'

let LOADING = false

const StyledLink = styled(Link)`
  text-decoration: none;
`

const CardWrapper = styled.div`
  outline: none;
`

const CardImage = styled.div.attrs(props => ({
  style: {
    background: `linear-gradient(rgba(141,122,157,0.40), rgba(141,122,157,0.40)), url('${props.logoImageUrl}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  transition: all 0.5s;
`

const StyledCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 300px;
  height: 400px;
  margin: 0 0.625rem;
  background: #ffffff;
  border-radius: var(--border-radius-card);
  box-shadow: var(--box-shadow-large);
  overflow: hidden;

  ${media.lessThan('medium')`
    margin: 0 0.5rem;
    height: 318px;
  `}

  &:hover {
    ${CardImage} {
      transform: scale(1.2);
    }
  }
`

const CardImageWrapper = styled.div`
  position: relative;
  width: 300px;
  min-height: 209px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  background: linear-gradient(
      rgba(141, 122, 157, 0.4),
      rgba(141, 122, 157, 0.4)
    ),
    #f9f9f9;

  ${() =>
    LOADING &&
    css`
      display: flex;
      justify-content: center;
      align-items: center;
    `}

  ${media.lessThan('medium')`
    min-height: 140px;
  `}
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  justify-content: space-between;
  padding: 1.875rem;
  height: 100%;
  min-width: 0;

  ${media.lessThan('medium')`
    padding: 1.5rem;
    padding-top: 1.875rem;
    padding-bottom: 2.25rem;
  `}
`

const Schedule = styled.span`
  font-size: 0.625rem;
  line-height: 1rem;
  font-weight: var(--font-weight-boldest);
  letter-spacing: var(--letter-spacing-wide);
  padding: 7px 16px;
  max-width: 100%;
  margin: 0;
  background: var(--color-bg);
  border-radius: var(--border-radius-small);
  color: #9b9b9b;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-transform: uppercase;
`

const Title = styled.h1`
  font-size: 1.125rem;
  line-height: 1.5625rem;
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-fg);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  ${media.lessThan('medium')`
    font-size: 1rem;
  `}
`

const Text = styled.p`
  margin: 0;
  color: white;
  text-align: center;
`

function PromoCard(props) {
  const {
    logoImageUrl = '',
    subtitle = '',
    title = '',
    link = '',
    id = 0,
    onClick,
    onRetry,
    loading = false,
    error = false,
  } = props

  LOADING = loading

  const truncatedTitle = truncateString(title, 56)

  const CardComponent = () => (
    <CardWrapper>
      <StyledLink to={link} onClick={error ? onRetry : onClick}>
        <StyledCard>
          <CardImageWrapper>
            {!LOADING ? (
              <CardImage logoImageUrl={logoImageUrl} />
            ) : (
              <>
                <img src={ZingIcon} alt="" />
                {error && (
                  <>
                    <Text>Failed to load Best Deals.</Text>
                    <Text>Tap to retry.</Text>
                  </>
                )}
              </>
            )}
          </CardImageWrapper>
          <CardContent>
            {!LOADING ? (
              <>
                <Title>{truncatedTitle}</Title>
                {!!subtitle && <Schedule>{subtitle}</Schedule>}
              </>
            ) : (
              <LoadingBase lines={2} />
            )}
          </CardContent>
        </StyledCard>
      </StyledLink>
    </CardWrapper>
  )

  if (LOADING) {
    return <CardComponent />
  }

  return (
    <Fade right distance="1rem" delay={300 + 50 * id} duration={300} force>
      <CardComponent />
    </Fade>
  )
}

PromoCard.defaultProps = {
  link: '',
}

PromoCard.propTypes = {
  logoImageUrl: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  link: PropTypes.string,
  onClick: PropTypes.func,
  onRetry: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.bool,
}

export default PromoCard
